import PasswordAccess from './components/auth/PasswordAccess';
import OnlinePaymentSetup from './components/OnlinePaymentSetup';
import GrowthCampaignBanner from "./components/GrowthCampaignBanner";
import DemoWorkspaceManager, { DemoWorkspaceBanner, DemoWorkspaceSwitcher } from './components/settings/DemoWorkspace';
// src/App.js
import React, { useState, useEffect, useCallback } from 'react';
import * as api from './api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';
import { getFromCache, saveToCache, cacheOrders, cachePayments, cacheExpenses, cacheInventory, cacheFinishedGoods, cacheCapital, cacheAssets, cacheAmbassadors, cacheDashboard } from './services/cacheService';
import { startSyncService } from './services/sync';
import { getDefaultTabForUser, getPolicyKey } from './utils/authHelpers';
import { PRESETS, FLAG_LABELS } from './utils/constants';

// Components
import Dashboard from './components/dashboard/Dashboard';
import Orders from './components/orders/Orders';
import Payments from './components/payments/Payments';
import Expenses from './components/expenses/Expenses';
import Inventory from './components/inventory/Inventory';
import NewSale from './components/sales/NewSale';
import AmbassadorDashboard from './components/ambassador/AmbassadorDashboard';
import PendingSales from './components/sales/PendingSales';
import SalesLog from './components/sales/SalesLog';
import CapitalModule from './components/finance/CapitalModule';
import InternalTransfers from './components/finance/InternalTransfers';
import Customers from './components/customers/Customers';
import Ledger from './components/finance/Ledger';
import AmbassadorMonitor from './components/ambassador/AmbassadorMonitor';
import ProductsCatalogView from './components/products/ProductsCatalogView';
import BusinessSelector from './components/auth/BusinessSelector';
import LoginScreen from './components/auth/LoginScreen';
import RegisterBusiness from './components/auth/RegisterBusiness';
import PolicyPage from './components/auth/PolicyPage';
import WelcomePage from './components/auth/WelcomePage';
import NotificationBell from './components/common/NotificationBell';
import SidebarNav from './components/common/SidebarNav';
import UserGuide from './components/common/UserGuide';
import AdminPortal from './components/admin/AdminPortal';
import AdminAccountPortal from './components/admin/AdminAccountPortal';
import TrackOrder from './components/public/TrackOrder';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import { PRODUCT_BRAND } from './appBranding';

// Secondary screens are loaded only when opened, keeping the initial ERP bundle focused on login/dashboard workflows.
const FinishedGoods = React.lazy(() => import('./components/finishedgoods/FinishedGoods'));
const DemandForecast = React.lazy(() => import('./components/finishedgoods/DemandForecast'));
const Allocation = React.lazy(() => import('./components/finance/Allocation'));
const Assets = React.lazy(() => import('./components/finance/Assets'));
const Shops = React.lazy(() => import('./components/shops/Shops'));
const AuditLog = React.lazy(() => import('./components/admin/AuditLog'));
const History = React.lazy(() => import('./components/reports/History'));
const UsersModule = React.lazy(() => import('./components/users/UsersModule'));
const AIBusinessAnalysis = React.lazy(() => import('./components/analytics/AIBusinessAnalysis'));
const FeaturesManager = React.lazy(() => import('./components/settings/FeaturesManager'));
const PlanBilling = React.lazy(() => import('./components/settings/PlanBilling'));
const MarketplaceSettings = React.lazy(() => import('./components/MarketplaceSettings'));

// Platform-admin authority lives on the backend only. The browser must not
// maintain a second email allow-list because frontend build-time env and
// Render runtime env can drift. /auth/is-admin is the single source of truth.

// Single source of truth for "which tabs can this user actually reach."
// Mirrors the same role/feature logic used to build the sidebar nav, so the
// two can never drift apart, and is checked at every entry point: initial
// hash read, hashchange events, AND the final render gate — defense in
// depth, since a hash check alone is bypassable by anyone editing state
// directly in devtools.
function getAllowedTabsForUser(u, isAdminUser) {
  if (!u) return [];
  const demo = u.workspaceContext?.mode === "demo";
  const f = u.features || {
    hasOrders: true, hasRawMaterials: true, hasWalkinSales: true,
    hasAmbassadors: true, hasProfitAlloc: true, hasAssets: true,
    salesCanLogPayments: false,
  };

  if (u.role === "manager") {
    return [
      "dashboard",
      ...(f.hasOrders ? ["orders", "payments"] : []),
      ...(f.hasWalkinSales ? ["finishedgoods", "newsale", "pendingsales", "saleslog", "forecast"] : []),
      ...(!demo ? ["marketplace", ...(!u.shopId ? ["marketplace-settings"] : [])] : []),
      ...(f.hasRawMaterials ? ["inventory"] : []),
      "expenses", ...(u.shopId ? [] : ["capital"]), "transfers",
      ...(f.hasAmbassadors ? ["ambassadors"] : []),
      ...(!u.shopId && f.hasProfitAlloc ? ["allocation"] : []),
      ...(!u.shopId && f.hasAssets ? ["assets"] : []),
      "customers", ...(u.isBusinessOwner || (u.role === "manager" && !u.shopId) ? ["shops"] : []),
      ...(!u.shopId ? ["history", "audit"] : []), "ledger", ...(!demo ? ["aianalysis"] : []), ...(!u.shopId ? ["users", "settings", ...(!demo ? ["plan"] : [])] : []),
      ...(isAdminUser ? ["admin"] : []),
    ];
  }
  if (u.role === "sales") {
    return [
      "dashboard",
      ...(f.hasOrders ? ["orders"] : []),
      ...(f.salesCanLogPayments && f.hasOrders ? ["payments"] : []),
      "saleslog", ...(!demo ? ["marketplace"] : []),
      ...(f.hasWalkinSales ? ["newsale"] : []),
      "products", "customers",
    ];
  }
  if (u.role === "cashier") {
    return [
      "dashboard",
      ...(f.hasOrders ? ["payments"] : []),
      ...(f.hasWalkinSales ? ["newsale", "pendingsales"] : []),
      ...(f.hasRawMaterials ? ["inventory"] : []),
      ...(!demo ? ["marketplace"] : []), "expenses", "saleslog", "products",
    ];
  }
  if (u.role === "ambassador") {
    return ["ambdash", "products", ...(!demo ? ["marketplace"] : [])];
  }
  if (u.role === "accountant") {
    // Read-only role (Tier 22) — the backend enforces this globally in
    // requireAuth regardless of what's shown here, but the tab list
    // itself only surfaces screens that make sense to audit: no
    // "New Order"/"New Sale" creation screens, since an accountant can
    // never submit those forms anyway (every write attempt 403s).
    // Shop allocation and its business-wide reports are operational
    // management duties, not accountant duties. Accountants therefore
    // have no Shops tab; their normal order/sale reads remain scoped by
    // shopId where applicable.
    return [
      "dashboard",
      ...(f.hasOrders ? ["orders"] : []),
      ...(f.hasWalkinSales ? ["saleslog"] : []),
      "expenses", ...(u.shopId ? [] : ["capital"]), "transfers", "ledger", ...(!u.shopId ? ["history"] : []),
      // aianalysis (Tier 24) — this is where the Accountant Audit tab
      // actually lives (AIBusinessAnalysis.js's third tab, gated
      // client-side AND server-side to manager/accountant only).
      // requireFeature("hasAI") on the backend still applies on top of
      // this — a business without AI on their plan gets a toast error
      // (via showAlert) if they click "Run Audit"/"Generate Analysis",
      // same as this component's other two tabs already behave; no
      // dedicated upgrade-prompt screen exists here (unlike
      // Customers.js/Shops.js) — not built this pass.
      "aianalysis",
    ];
  }
  return [];
}

// ── CSS ──
 
export const css = `/* ── CSS custom properties — theme tokens ── */
  :root {
    --bg:          #f0f2f5;
    --surface:     #ffffff;
    --surface2:    #f9fafb;
    --border:      #e5e7eb;
    --text:        #1a1a2e;
    --text-muted:  #6b7280;
    --text-faint:  #9ca3af;
    --sidebar-bg:  #1a1a2e;
    --sidebar-text:#9ca3af;
    --sidebar-act: #e94560;
    --topbar-bg:   #ffffff;
    --topbar-border:#e5e7eb;
    --input-bg:    #ffffff;
    --th-bg:       #1a1a2e;
    --preview-bg:  #f9fafb;
    --hover-row:   #f9fafb;
    --shadow:      0 1px 4px rgba(0,0,0,.08);
    --accent:      #e94560;
    --radius:      12px;
    /* --text-3 is referenced in 100+ places across this codebase
       (Dashboard, Orders, Payments, History, and many more) but was
       never actually defined anywhere — every one of those was silently
       falling back to an inherited color instead of the intended muted
       tone, which happened to look mostly fine in light mode by
       accident and broke visibly in dark mode. Aliasing it here fixes
       every existing call site in one place, and correctly tracks the
       dark-mode override below since CSS custom properties resolve
       through the cascade, not at definition time. */
    --text-3:      var(--text-muted);
  }
 
  /* ── Dark mode overrides ── */
  [data-theme="dark"] {
    --bg:           #0f1117;
    --surface:      #1a1d27;
    --surface2:     #232635;
    --border:       #2d3148;
    --text:         #e8eaf0;
    --text-muted:   #9ca3af;
    --text-faint:   #6b7280;
    --sidebar-bg:   #13151f;
    --sidebar-text: #8892a4;
    --sidebar-act:  #e94560;
    --topbar-bg:    #1a1d27;
    --topbar-border:#2d3148;
    --input-bg:     #232635;
    --th-bg:        #13151f;
    --preview-bg:   #232635;
    --hover-row:    #232635;
    --shadow:       0 1px 6px rgba(0,0,0,.3);
  }
 
  * { box-sizing: border-box; margin: 0; padding: 0; }
  
  .app-shell {
    max-width: 100vw;
    overflow-x: hidden;
  }
  
  .main-area {
    max-width: 100%;
  }
  
  .card {
    min-width: 0;
    word-break: break-word;
  }
 
  body {
    font-family: 'Segoe UI', sans-serif;
    background: var(--bg);
    color: var(--text);
    transition: background .2s, color .2s;
  }
 
  /* ── Layout shell ── */
  .app-shell { display:flex; min-height:100vh; }
 
  /* ── Sidebar ── */
  .sidebar {
    width: 240px;
    background: var(--sidebar-bg);
    display: flex;
    flex-direction: column;
    position: fixed;
    top:0; left:0; bottom:0;
    z-index: 200;
    transform: translateX(-100%);
    transition: transform .25s ease, background .2s;
    overflow-y: auto;
    overflow-x: hidden;
  }
 
  .sidebar-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 14px 12px;
    border-bottom: 1px solid rgba(255,255,255,.07);
    flex-shrink: 0;
  }
    .sidebar.open {
    transform: translateX(0) !important;
  }
  .sidebar-logo { width:34px; height:34px; border-radius:8px; object-fit:cover; border:2px solid var(--accent); flex-shrink:0; }
  .sidebar-biz-name { font-size:13px; font-weight:700; color:#fff; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .sidebar-plan { font-size:9px; color:var(--sidebar-text); margin-top:1px; }
 
  .sidebar-nav { flex:1; padding:8px 8px 8px; }
 
  /* Section label */
  .nav-section-label {
    font-size: 9px;
    font-weight: 700;
    color: #3d4560;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 14px 12px 4px;
    user-select: none;
  }
  [data-theme="dark"] .nav-section-label { color: #3a4060; }
 
  /* Nav item */
  .nav-item {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    padding: 8px 11px;
    border: none;
    background: transparent;
    color: var(--sidebar-text);
    font-size: 12.5px;
    font-weight: 500;
    cursor: pointer;
    border-radius: 8px;
    text-align: left;
    white-space: nowrap;
    position: relative;
    margin-bottom: 1px;
    transition: background .12s, color .12s;
  }
  .nav-item:hover  { background: rgba(255,255,255,.06); color:#fff; }
  .nav-item.active { background: var(--accent); color:#fff; font-weight:600; }
  .nav-item .nav-icon { font-size:15px; flex-shrink:0; width:19px; text-align:center; }
  .nav-item .nav-badge {
    margin-left: auto;
    background: var(--accent);
    color: #fff;
    border-radius: 20px;
    padding: 1px 6px;
    font-size: 9px;
    font-weight: 700;
    flex-shrink: 0;
  }
  .nav-item.active .nav-badge { background: rgba(255,255,255,.3); }
 
  /* Group header button (collapsible section) */
  .nav-group-header {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    padding: 9px 11px 7px;
    border: none;
    background: transparent;
    color: #5a6380;
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .7px;
    cursor: pointer;
    border-radius: 8px;
    text-align: left;
    margin-top: 6px;
    margin-bottom: 1px;
    transition: color .12s;
    user-select: none;
  }
  .nav-group-header:hover { color: #8892b0; }
  .nav-group-header .group-icon { font-size:14px; flex-shrink:0; }
  .nav-group-header .group-chevron { margin-left:auto; font-size:10px; transition:transform .2s; }
  .nav-group-header.open .group-chevron { transform: rotate(180deg); }
  .nav-group-header .group-badge {
    background: rgba(233,69,96,.25);
    color: var(--accent);
    border-radius:20px;
    padding:1px 6px;
    font-size:9px;
    font-weight:700;
  }
 
  /* Group children */
  .nav-group-children {
    overflow: hidden;
    transition: max-height .22s ease, opacity .18s ease;
  }
  .nav-group-children.open  { max-height: 400px; opacity:1; }
  .nav-group-children.closed{ max-height: 0;     opacity:0; }
 
  /* Indented child items */
  .nav-item.child {
    padding-left: 30px;
    font-size: 12px;
  }
 
  /* Sidebar footer */
  .sidebar-footer {
    padding: 10px 8px;
    border-top: 1px solid rgba(255,255,255,.07);
    flex-shrink: 0;
  }
  .sidebar-user {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 8px;
    margin-bottom: 6px;
  }
  .sidebar-user-info { flex:1; overflow:hidden; }
  .sidebar-user-name { font-size:12px; font-weight:600; color:#fff; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .sidebar-user-role { font-size:10px; color:var(--sidebar-text); }

  .notification-wrap { position:relative; }
  .notification-bell {
    position: relative;
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(255,255,255,.12);
    border-radius: 10px;
    background: rgba(255,255,255,.06);
    color: #fff;
    cursor: pointer;
    transition: background .15s, border-color .15s, transform .15s;
  }
  .notification-bell:hover { background: rgba(255,255,255,.12); border-color: rgba(255,255,255,.22); transform: translateY(-1px); }
  .notification-bell.has-items { background: rgba(233,69,96,.16); border-color: rgba(233,69,96,.45); }
  .notification-icon { font-size: 17px; line-height: 1; }
  .notification-dot {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: #ef4444;
    color: #fff;
    border: 2px solid var(--sidebar-bg);
    font-size: 10px;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }
  .notification-pulse {
    position:absolute;
    inset: 4px;
    border-radius: 999px;
    border: 1px solid rgba(239,68,68,.45);
    animation: notification-pulse 1.7s ease-out infinite;
    pointer-events:none;
  }
  @keyframes notification-pulse {
    from { opacity:.7; transform:scale(.8); }
    to { opacity:0; transform:scale(1.6); }
  }
  .notification-menu {
    position: absolute;
    right: 0;
    bottom: calc(100% + 10px);
    width: min(340px, calc(100vw - 28px));
    background: var(--surface);
    color: var(--text);
    border: 1px solid var(--border);
    border-radius: 12px;
    box-shadow: 0 18px 50px rgba(15,17,23,.22);
    overflow: hidden;
    z-index: 999;
  }
  .notification-menu-header {
    padding: 14px;
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .notification-title { font-size: 13px; font-weight: 800; }
  .notification-subtitle { margin-top: 2px; font-size: 11px; color: var(--text-muted); }
  .notification-refresh {
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--surface2);
    color: var(--text-muted);
    font-size: 11px;
    font-weight: 700;
    padding: 6px 9px;
    cursor: pointer;
  }
  .notification-refresh:hover { color: var(--text); border-color: var(--accent); }
  .notification-empty { padding: 24px 16px; text-align:center; color: var(--text-muted); font-size: 13px; }
  .notification-empty-icon {
    width: 38px;
    height: 38px;
    margin: 0 auto 8px;
    border-radius: 999px;
    background: rgba(16,185,129,.12);
    color: #10b981;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 900;
  }
  .notification-item {
    width: 100%;
    display: grid;
    grid-template-columns: 34px 1fr auto;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border: 0;
    border-bottom: 1px solid var(--border);
    background: transparent;
    color: var(--text);
    text-align: left;
    cursor: pointer;
  }
  .notification-item:hover { background: var(--surface2); }
  .notification-item:last-child { border-bottom: 0; }
  .notification-item-icon {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: rgba(233,69,96,.12);
    color: var(--accent);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
  }
  .notification-item-label { font-size: 12.5px; line-height: 1.35; font-weight: 650; }
  .notification-item-meta { margin-top: 2px; font-size: 10.5px; color: var(--text-muted); }
  .notification-arrow { color: var(--text-faint); font-size: 16px; }
  .notification-audio {
    padding: 9px 14px;
    background: var(--surface2);
    border-top: 1px solid var(--border);
    font-size: 10.5px;
    color: var(--text-muted);
  }
  .notification-error {
    padding: 10px 14px;
    border-top: 1px solid var(--border);
    background: #fef2f2;
    color: #991b1b;
    font-size: 11px;
    line-height: 1.45;
  }
  [data-theme="dark"] .notification-error { background:#4a1919; color:#fca5a5; }

  .official-screen {
    min-height: 100vh;
    display: grid;
    grid-template-columns: minmax(320px, 0.92fr) minmax(420px, 1.08fr);
    background: #f6f8fb;
    color: #172033;
  }
  .official-brand-panel {
    background: #101827;
    color: #fff;
    padding: clamp(28px, 5vw, 64px);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 36px;
  }
  .official-mark {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    background: var(--accent);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 900;
    font-size: 22px;
    margin-bottom: 18px;
  }
  .official-brand-panel h1 {
    font-size: clamp(28px, 4vw, 46px);
    line-height: 1.05;
    margin-bottom: 14px;
    letter-spacing: 0;
  }
  .official-brand-panel p {
    color: #c7d2e4;
    font-size: 14px;
    line-height: 1.75;
    max-width: 480px;
  }
  .official-proof {
    display: grid;
    gap: 10px;
    font-size: 12px;
    color: #dbe4f0;
  }
  .official-proof span {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .official-main-panel {
    padding: clamp(24px, 5vw, 64px);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .official-card {
    width: min(680px, 100%);
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 18px 45px rgba(16,24,39,.1);
    padding: clamp(22px, 4vw, 38px);
  }
  .official-eyebrow {
    color: var(--accent);
    text-transform: uppercase;
    letter-spacing: 0;
    font-weight: 800;
    font-size: 11px;
    margin-bottom: 8px;
  }
  .official-card h2 {
    font-size: clamp(22px, 3vw, 31px);
    line-height: 1.15;
    margin-bottom: 10px;
    color: #172033;
  }
  .official-card p {
    color: #5c667a;
    line-height: 1.7;
    font-size: 14px;
  }
  .policy-list {
    display: grid;
    gap: 12px;
    margin: 22px 0;
  }
  .policy-item {
    display: grid;
    grid-template-columns: 30px 1fr;
    gap: 12px;
    padding: 12px;
    border: 1px solid #eef1f5;
    border-radius: 8px;
    background: #fbfcfe;
  }
  .policy-item strong {
    display: block;
    color: #172033;
    font-size: 13px;
    margin-bottom: 2px;
  }
  .policy-icon {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: rgba(233,69,96,.12);
    color: var(--accent);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 900;
  }
  .welcome-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin: 22px 0;
  }
  .welcome-metric {
    border: 1px solid #eef1f5;
    border-radius: 8px;
    padding: 12px;
    background: #fbfcfe;
  }
  .welcome-metric span {
    display: block;
    color: #6b7280;
    font-size: 11px;
    text-transform: uppercase;
    font-weight: 800;
  }
  .welcome-metric strong {
    display: block;
    color: #172033;
    font-size: 15px;
    margin-top: 4px;
    overflow-wrap: anywhere;
  }
  .official-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-top: 22px;
  }
  .official-actions .btn { min-width: 160px; }
  @media (max-width: 860px) {
    .official-screen { grid-template-columns: 1fr; }
    .official-brand-panel { min-height: auto; }
    .official-main-panel { align-items: flex-start; }
    .welcome-grid { grid-template-columns: 1fr; }
  }
 
  /* Theme toggle button */
  .theme-toggle {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    border: 1px solid rgba(255,255,255,.1);
    border-radius: 8px;
    background: rgba(255,255,255,.04);
    color: var(--sidebar-text);
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    margin-bottom: 6px;
    transition: background .15s, color .15s;
  }
  .theme-toggle:hover { background: rgba(255,255,255,.09); color:#fff; }
  .theme-toggle .toggle-track {
    margin-left: auto;
    width: 32px;
    height: 18px;
    border-radius: 20px;
    background: #2d3148;
    position: relative;
    transition: background .2s;
    flex-shrink: 0;
  }
  .theme-toggle .toggle-track.on { background: var(--accent); }
  .theme-toggle .toggle-thumb {
    position: absolute;
    top: 2px; left: 2px;
    width: 14px; height: 14px;
    border-radius: 50%;
    background: #fff;
    transition: transform .2s;
  }
  .theme-toggle .toggle-track.on .toggle-thumb { transform: translateX(14px); }
 
  .sidebar-logout {
    width: 100%;
    padding: 8px;
    background: rgba(233,69,96,.12);
    border: 1px solid rgba(233,69,96,.25);
    color: var(--accent);
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
  }
  .sidebar-logout:hover { background: rgba(233,69,96,.22); }

  /* Sidebar is always dark regardless of app theme (see --sidebar-bg above),
     so anything inside it must use --sidebar-text / rgba(255,255,255,..)
     tokens, never the theme-flipping --text/--border/--surface2 — those go
     dark-on-dark in light mode, which is what made this button's text
     disappear. */
  .sidebar-guide-btn {
    display: flex; align-items: center; gap: 6px;
    width: 100%; padding: 9px 12px;
    background: none; border: 1px solid rgba(255,255,255,.1);
    border-radius: 8px; color: var(--sidebar-text); cursor: pointer;
    font-size: 12.5px; font-weight: 600; text-align: left;
    transition: background .12s ease, color .12s ease;
    margin-bottom: 6px;
  }
  .sidebar-guide-btn:hover { background: rgba(255,255,255,.09); color: #fff; }
 
  /* ── Topbar ── */
  .topbar {
    position: fixed;
    top: 0; left: 0; right: 0;
    height: 52px;
    background: var(--topbar-bg);
    border-bottom: 1px solid var(--topbar-border);
    display: flex;
    align-items: center;
    padding: 0 16px;
    gap: 10px;
    z-index: 100;
    transition: left .25s ease, background .2s, border-color .2s;
  }
  .topbar-toggle {
    background: none; border: none; cursor: pointer;
    padding: 6px; border-radius: 8px;
    display: flex; flex-direction: column; gap: 4px; flex-shrink: 0;
    flex-shrink: 0;
  }
  .topbar-toggle:hover { background: var(--surface2); }
  .topbar-toggle span { display:block; width:18px; height:2px; background:var(--text); border-radius:2px; }
  .topbar-page-title { font-size:14px; font-weight:600; color:var(--text); flex:1; }
  .role-badge { background:var(--accent); color:#fff; font-size:10px; padding:3px 8px; border-radius:20px; font-weight:600; flex-shrink:0; }
 
  /* ── Main content ── */
  .main-area {
    margin-top: 52px;
    min-height: calc(100vh - 52px);
    width: 100%;
    padding: 16px;
  }
  .content { padding:0; max-width:960px; margin:0 auto; width:100%; }
 
  /* ── Cards ── */
  .card {
    background: var(--surface);
    border-radius: var(--radius);
    padding: 12px;
    margin-bottom: 16px;
    box-shadow: var(--shadow);
    border: 1px solid var(--border);
    transition: background .2s, border-color .2s;
  }
  .card h2 { font-size:14px; font-weight:700; margin-bottom:12px; color:var(--text); }
  .card h3 { font-size:13px; font-weight:700; margin-bottom:10px; color:var(--text); }
 
  /* Collapsible card */
  .card-collapsible-header { display:flex; justify-content:space-between; align-items:center; cursor:pointer; user-select:none; margin-bottom:0; }
  .card-collapsible-header h2 { margin-bottom:0; }
  .card-collapse-icon { font-size:13px; color:var(--text-faint); font-weight:600; flex-shrink:0; padding:2px 6px; }
  .card-body { margin-top:12px; }
 
  
  
  .charts-grid {
    display: grid;
    grid-template-columns: 1.2fr 1fr 1fr;
    gap: 12px;
  }
  @media (max-width: 768px) {
    .charts-grid {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
  }
  
  .newsale-layout {
    display: grid;
    grid-template-columns: 1fr 380px;
    gap: 20px;
    align-items: start;
  }
  @media (max-width: 768px) {
    .newsale-layout {
      grid-template-columns: 1fr;
      gap: 16px;
    }
  }

  .products-display-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
  }
  @media (max-width: 768px) {
    .products-display-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
    }
  }
  
  .product-catalog-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
    max-height: calc(100vh - 280px);
    overflow-y: auto;
    padding-right: 4px;
  }
  @media (max-width: 768px) {
    .product-catalog-grid {
      grid-template-columns: repeat(2, 1fr);
      max-height: none;
      overflow-y: visible;
      padding-right: 0;
    }
  }
  
.newsale-cart {
    display: flex;
    flex-direction: column;
    height: fit-content;
    position: sticky;
    top: 60px;
  }
  @media (max-width: 768px) {
    .newsale-cart {
      position: static;
    }
  }

  .cart-items-list {
    max-height: 300px;
    overflow-y: auto;
    margin-bottom: 16px;
  }
  @media (max-width: 768px) {
    .cart-items-list {
      max-height: none;
      overflow-y: visible;
    }
  }

  .product-list-view {
    max-height: calc(100vh - 280px);
    overflow-y: auto;
  }
  @media (max-width: 768px) {
    .product-list-view {
      max-height: none;
      overflow-y: visible;
    }
  }

  .walkin-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
  }
  @media (max-width: 768px) {
    .walkin-grid {
      display: flex;
      flex-wrap: nowrap;
      overflow-x: auto;
      overflow-y: hidden;
      -webkit-overflow-scrolling: touch;
      scroll-snap-type: x mandatory;
      gap: 10px;
      padding-bottom: 6px;
      touch-action: pan-x pinch-zoom;
    }
    .walkin-grid > * {
      flex: 0 0 52vw;
      min-width: 150px;
      scroll-snap-align: start;
      flex-shrink: 0;
    }
  }
  
  .split-grid {
    display: grid;
    grid-template-columns: 1fr 1.6fr;
    gap: 12px;
  }
  @media (max-width: 768px) {
    .split-grid {
      grid-template-columns: 1fr;
    }
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  @media (max-width: 768px) {
    .kpi-grid {
      grid-template-columns: 1fr;
    }
  }
  
  .hero-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 12px;
  }
  @media (max-width: 768px) {
    .hero-grid {
      display: flex;
      flex-wrap: nowrap;
      overflow-x: auto;
      overflow-y: hidden;
      -webkit-overflow-scrolling: touch;
      scroll-snap-type: x mandatory;
      gap: 12px;
      padding-bottom: 8px;
      touch-action: pan-x pinch-zoom;
    }
    .hero-grid > * {
      flex: 0 0 78vw;
      min-width: 0;
      max-width: 78vw;
      scroll-snap-align: start;
      flex-shrink: 0;
      overflow: hidden;
    }
  }
  
  /* ── Stats ── */
  .stats-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
      margin-bottom: 16px;
    }
    .stats-grid-3 {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
      margin-bottom: 16px;
    }
  .stat-card {
      background: var(--surface);
      border-radius: var(--radius);
      padding: 14px;
      box-shadow: var(--shadow);
      border: 1px solid var(--border);
      transition: background .2s, border-color .2s;
      flex-shrink: 0;
    }
  .stat-card .lbl { font-size:10px; color:var(--text-muted); margin-bottom:4px; text-transform:uppercase; letter-spacing:.5px; }
  .stat-card .val { font-size:16px; font-weight:700; color:var(--text); }
  .val.accent  { color:#e94560; }
  .val.success { color:#10b981; }
  .val.warning { color:#f59e0b; }
  .val.info    { color:#3b82f6; }

  /* ── Quick Actions (dashboard module launcher) ── */
  .quick-actions-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 10px;
  }
  .quick-action-card {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 14px;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
    box-shadow: var(--shadow);
  }
  .quick-action-card:hover, .quick-action-card:focus-visible {
    transform: translateY(-2px);
    border-color: var(--accent);
    box-shadow: 0 6px 16px rgba(233,69,96,.16);
    outline: none;
  }
  .quick-action-card:active { transform: translateY(0); }
  .quick-action-icon {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
    background: var(--surface2);
  }
  .quick-action-label { font-size: 13px; font-weight: 700; color: var(--text); }
  .quick-action-desc { font-size: 10.5px; color: var(--text-muted); line-height: 1.35; }
  .quick-action-badge {
    position: absolute;
    top: 10px;
    right: 10px;
    background: var(--accent);
    color: #fff;
    font-size: 10px;
    font-weight: 800;
    min-width: 17px;
    height: 17px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
  }
  @media (max-width: 768px) {
    .quick-actions-grid { grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); }
  }

  /* ── Daily Brief toast (AI "first sign-in of the day" overview) ── */
  @keyframes daily-brief-slide-in {
    from { transform: translateY(16px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }
  .daily-brief-toast {
    position: fixed;
    right: 20px;
    bottom: 20px;
    width: 410px;
    max-width: calc(100vw - 40px);
    max-height: min(72vh, 620px);
    overflow-y: auto;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    box-shadow: 0 10px 30px rgba(0,0,0,.18);
    padding: 14px 14px 16px;
    z-index: 3000;
    animation: daily-brief-slide-in .35s ease;
  }
  .daily-brief-toast-head {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-bottom: 8px;
  }
  .daily-brief-toast-icon { font-size: 15px; }
  .daily-brief-toast-title {
    flex: 1;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: .3px;
    text-transform: uppercase;
    color: var(--accent);
  }
  .daily-brief-toast-close {
    border: none;
    background: none;
    color: var(--text-muted);
    cursor: pointer;
    font-size: 12px;
    padding: 2px 4px;
    line-height: 1;
  }
  .daily-brief-toast-body {
    font-size: 13px;
    line-height: 1.55;
    color: var(--text);
    margin: 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  @media (max-width: 768px) {
    .daily-brief-toast { left: 16px; right: 16px; bottom: max(16px, env(safe-area-inset-bottom)); width: auto; max-height: calc(100dvh - 32px); overflow-y: auto; }
  }
 
  /* ── Forms ── */
  .form-group { margin-bottom:12px; }
  .form-group label { display:block; font-size:12px; font-weight:600; margin-bottom:4px; color:var(--text-muted); }
  .form-group input,
  .form-group select,
  .form-group textarea {
    width:100%; padding:10px 12px;
    border:1.5px solid var(--border);
    border-radius:8px; font-size:14px; outline:none;
    background: var(--input-bg);
    color: var(--text);
    transition: background .2s, border-color .2s, color .2s;
  }
  .form-group input:focus,
  .form-group select:focus { border-color:var(--accent); }
  .form-row { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
 
  /* ── Buttons ── */
  .btn { padding:10px 18px; border:none; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; }
  .btn-primary   { background:var(--accent); color:#fff; }
  .btn-secondary { background:var(--surface2); color:var(--text); border:1px solid var(--border); }
  .btn-success   { background:#10b981; color:#fff; }
  .btn-gold      { background:#f5a623; color:#fff; }
  .btn-danger    { background:#dc2626; color:#fff; }
  .btn-sm { padding:6px 12px; font-size:11px; border-radius:6px; border:none; cursor:pointer; font-weight:600; }
  .btn-edit    { background:#dbeafe; color:#1d4ed8; }
  .btn-approve { background:#d1fae5; color:#065f46; }
  .btn-reject  { background:#fee2e2; color:#991b1b; }
  .btn:disabled { opacity:.6; cursor:not-allowed; }
  [data-theme="dark"] .btn-edit    { background:#1e3a5f; color:#60a5fa; }
  [data-theme="dark"] .btn-approve { background:#14432a; color:#34d399; }
  [data-theme="dark"] .btn-reject  { background:#4a1919; color:#f87171; }
 
  /* ── Tables ── */
  .table-wrap {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      max-width: 100%;
    }
    .table-wrap table {
      min-width: 600px;
    }
      @media (max-width: 768px) {
        .table-wrap {
          max-width: calc(100vw - 32px);
        }
      }
  table { width:100%; border-collapse:collapse; font-size:12px; }
  th { background:var(--th-bg); color:#fff; padding:8px 10px; text-align:left; white-space:nowrap; }
  td { padding:8px 10px; border-bottom:1px solid var(--border); white-space:nowrap; color:var(--text); }
  tr:hover td { background:var(--hover-row); }
 
  /* ── Badges ── */
  .badge { display:inline-block; padding:2px 8px; border-radius:20px; font-size:10px; font-weight:700; }
  .badge-paid,.badge-completed,.badge-cleared,.badge-approved,.badge-delivered { background:#d1fae5; color:#065f46; }
  .badge-partial,.badge-ongoing,.badge-pending,.badge-in-production { background:#fef3c7; color:#92400e; }
  .badge-unpaid,.badge-rejected,.badge-cancelled { background:#fee2e2; color:#991b1b; }
  .badge-active,.badge-ready,.badge-awaiting-payment { background:#dbeafe; color:#1e40af; }
  [data-theme="dark"] .badge-paid,.badge-completed,.badge-cleared,.badge-approved,.badge-delivered { background:#14432a; color:#34d399; }
  [data-theme="dark"] .badge-partial,.badge-ongoing,.badge-pending,.badge-in-production { background:#3d2c0a; color:#fbbf24; }
  [data-theme="dark"] .badge-unpaid,.badge-rejected,.badge-cancelled { background:#4a1919; color:#f87171; }
  [data-theme="dark"] .badge-active,.badge-ready,.badge-awaiting-payment { background:#1e3a5f; color:#60a5fa; }
 
  /* ── Alerts ── */
  .alert { padding:10px 14px; border-radius:8px; font-size:13px; margin-bottom:12px; font-weight:500; }
  .alert-success { background:#d1fae5; color:#065f46; }
  .alert-error   { background:#fee2e2; color:#991b1b; }
  .alert-info    { background:#dbeafe; color:#1e40af; }
  .alert-warning { background:#fef3c7; color:#92400e; }
  [data-theme="dark"] .alert-success { background:#14432a; color:#34d399; }
  [data-theme="dark"] .alert-error   { background:#4a1919; color:#f87171; }
  [data-theme="dark"] .alert-info    { background:#1e3a5f; color:#60a5fa; }
  [data-theme="dark"] .alert-warning { background:#3d2c0a; color:#fbbf24; }
 
  /* ── Misc ── */
  .search-bar { display:flex; flex-direction: column; gap:8px; margin-bottom:12px; flex-wrap:wrap; }
  .search-bar input,
  .search-bar select {
    flex:1; min-width:100px; padding:8px 12px;
    border:1.5px solid var(--border); border-radius:8px; font-size:13px; outline:none;
    background:var(--input-bg); color:var(--text);
    transition: background .2s, border-color .2s;
  }
  .pager { display:flex; gap:8px; align-items:center; justify-content:center; margin-top:12px; font-size:13px; }
  .login-screen { min-height:100vh; display:flex; align-items:center; justify-content:center; background:#1a1a2e; padding:24px; }
  .login-box { background:#fff; border-radius:16px; padding:32px 24px; max-width:400px; width:100%; text-align:center; max-height:90vh; overflow-y:auto; }
  .login-box h2 { font-size:22px; font-weight:800; color:#1a1a2e; margin-bottom:4px; }
  .login-box p  { font-size:13px; color:#6b7280; margin-bottom:24px; }
  .google-btn { display:flex; align-items:center; justify-content:center; gap:10px; width:100%; padding:13px; border:2px solid #e5e7eb; border-radius:10px; background:#fff; cursor:pointer; font-size:14px; font-weight:600; }
  .google-btn:hover { border-color:#e94560; color:#e94560; }
  .section-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:14px; flex-wrap:wrap; gap:8px; }
  .mode-tag { background:#f5a623; color:#fff; font-size:10px; padding:2px 8px; border-radius:20px; font-weight:700; }
  .divider { height:1px; background:var(--border); margin:14px 0; }
  .spinner { text-align:center; padding:40px; color:var(--text-muted); font-size:14px; }
  .preview-box { background:var(--preview-bg); border-radius:8px; padding:10px; margin-bottom:12px; font-size:12px; color:var(--text); border:1px solid var(--border); transition:background .2s,border-color .2s; }
  .kpi-toggle { background:none; border:none; cursor:pointer; font-size:13px; font-weight:600; color:var(--accent); padding:4px 0; }
  .pending-badge { background:#fef3c7; color:#92400e; border-radius:20px; padding:2px 8px; font-size:10px; font-weight:700; }
  .tab-switch { display:flex; gap:4px; background:var(--surface2); border-radius:8px; padding:4px; margin-bottom:14px; }
  .tab-switch button { flex:1; padding:7px; border:none; border-radius:6px; font-size:12px; font-weight:600; cursor:pointer; background:transparent; color:var(--text-muted); }
  .tab-switch button.active { background:var(--surface); color:var(--text); box-shadow:0 1px 3px rgba(0,0,0,.1); }
  .rank-row { display:flex; align-items:center; gap:12px; padding:10px 0; border-bottom:1px solid var(--border); }
  .rank-num { width:28px; height:28px; border-radius:50%; background:var(--text); color:var(--surface); display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; flex-shrink:0; }
  .rank-num.gold   { background:#f5a623; }
  .rank-num.silver { background:#9ca3af; }
  .rank-num.bronze { background:#b45309; }
  .rank-me { background:#e94560!important; }
  .winner-badge { background:linear-gradient(135deg,#f5a623,#e94560); color:#fff; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:700; margin-left:8px; }
  .hof-card { border:1px solid var(--border); border-radius:10px; padding:14px; margin-bottom:10px; background:var(--surface); }
  .hof-winner { display:flex; align-items:center; gap:10px; margin-bottom:8px; }
  .hof-trophy { font-size:28px; }
  .danger-zone { border:2px solid #fee2e2; border-radius:10px; padding:14px; margin-top:16px; }
  .danger-zone h3 { color:#dc2626; font-size:13px; margin-bottom:8px; }
  .biz-card { border:2px solid var(--border); border-radius:12px; padding:16px; cursor:pointer; transition:.15s; text-align:left; background:var(--surface); width:100%; margin-bottom:10px; display:block; }
  .biz-card:hover { border-color:var(--accent); }
  .toggle-btn { padding:5px 14px; border-radius:20px; border:1.5px solid var(--border); background:var(--surface); font-size:12px; cursor:pointer; font-weight:600; color:var(--text); white-space:nowrap; }
  .toggle-btn.on  { background:#10b981; color:#fff; border-color:#10b981; }
  .toggle-btn.off { background:#fee2e2; color:#dc2626; border-color:#fecaca; }
  [data-theme="dark"] .toggle-btn.off { background:#4a1919; color:#f87171; border-color:#7a2020; }
 
  
    /* Skeleton Loader Animation */
  .skeleton {
    background: linear-gradient(90deg, var(--border) 25%, var(--surface2) 50%, var(--border) 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: var(--radius-sm, 6px);
  }
  
  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  /* ── Mobile responsive ── */
  @media (max-width: 768px) {
    .topbar { left: 0 !important; }
    .main-area { margin-left: 0 !important; width: 100% !important; }
    .stats-grid, .stats-grid-3 { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .form-row { grid-template-columns: minmax(0, 1fr); }
  }
  /* Mobile layout correction: preserve usable dashboard density without
     turning key metrics into narrow horizontal carousels. */
  @media (max-width: 768px) {
    html, body, #root { width: 100%; min-width: 0; overflow-x: clip; }
    .app-shell { width: 100%; max-width: 100%; min-width: 0; overflow-x: clip; }
    .main-area, .page-content, .content, .main-content {
      min-width: 0 !important; max-width: 100% !important; width: 100% !important;
    }
    .main-area { padding-left: 12px !important; padding-right: 12px !important; }
    .page-content { padding: 12px !important; }
    .topbar { padding-left: 10px !important; padding-right: 10px !important; gap: 7px !important; }
    .topbar select[aria-label="Switch workspace"] { max-width: 124px !important; min-width: 0 !important; font-size: 10px !important; padding: 6px !important; }
    .topbar-page-title, .topbar-title {
      min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      font-size: 13px !important;
    }
    .role-badge { font-size: 9px !important; padding: 3px 6px !important; }
    .stats-grid, .stats-grid-3, .kpi-grid, .hero-grid, .quick-actions-grid {
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 10px !important;
      overflow: visible !important;
      max-width: 100% !important;
      min-width: 0 !important;
      scroll-snap-type: none !important;
      touch-action: auto !important;
    }
    .stats-grid > *, .stats-grid-3 > *, .hero-grid > *, .quick-actions-grid > * {
      width: auto !important; min-width: 0 !important; max-width: 100% !important;
      flex: initial !important; scroll-snap-align: unset !important;
    }
    .charts-grid { display: grid !important; grid-template-columns: minmax(0, 1fr) !important; }
    .walkin-grid { display: grid !important; grid-template-columns: repeat(2, minmax(0, 1fr)) !important; overflow: visible !important; }
    .card, .stat-card, .quick-action-card { min-width: 0; max-width: 100%; }
    .stat-card { padding: 12px !important; }
    .stat-card .val { font-size: clamp(14px, 4vw, 19px) !important; overflow-wrap: anywhere; }
    .form-row, .form-row-3, .detail-grid-2 { grid-template-columns: minmax(0, 1fr) !important; }
    .section-header, .page-header { align-items: flex-start; flex-wrap: wrap; gap: 10px; }
    .section-header > *, .page-header > * { min-width: 0; max-width: 100%; }
    .table-wrap, .ap-table-wrap { width: 100%; max-width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
    .table-wrap table { min-width: 620px; }
    input, select, textarea, button { max-width: 100%; }
    input, select, textarea { min-height: 42px; }
    button, .btn, .btn-primary, .btn-secondary { min-height: 40px; }
    .tab-switch, .tabs { max-width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
    .tab-switch button, .tab-btn { flex: 0 0 auto; }
    .notification-menu { max-width: calc(100vw - 24px); }
  }
  @media (max-width: 390px) {
    .main-area, .page-content { padding-left: 10px !important; padding-right: 10px !important; }
    .stats-grid, .stats-grid-3, .kpi-grid, .hero-grid, .quick-actions-grid {
      gap: 8px !important;
    }
    .card { padding: 12px !important; }
  }
`;
// ── App ──
export default function App() {
  const [user, setUser] = useState(undefined);
  const [selectorData, setSelectorData] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [alert, setAlert] = useState(null);
  // Pre-auth "Request Business Access" flow is gone — registration now
  // happens post-auth via RegisterBusiness (see the "unregistered" role
  // branch below).
  const [urlError, setUrlError] = useState("");
  const [notifCounts, setNotifCounts] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > 768);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(true);
  const [cartCount, setCartCount] = useState(0);
  const [redirectAfterLogin, setRedirectAfterLogin] = useState(null);
  const [isOfflineReady, setIsOfflineReady] = useState(false);

  const isAdminRoute = window.location.pathname === '/admin';

  // ── Dark/Light mode ──
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem("erp_theme") === "dark"; } catch (e) { return false; }
  });

  // Keep this callback stable. Notification polling updates the parent
  // regularly; without useCallback every child that depends on showAlert
  // treated that harmless update as a reason to reload itself.
  const showAlert = useCallback((msg, type = "success") => {
    setAlert({ msg, type });
    setTimeout(() => setAlert(null), 4000);
  }, []);

  // ── Dark mode effect ──
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", darkMode ? "dark" : "light");
    try { localStorage.setItem("erp_theme", darkMode ? "dark" : "light"); } catch (e) {}
  }, [darkMode]);

  useEffect(() => {
    const cachedUser = getFromCache('user');
    if (cachedUser && !navigator.onLine) {
      setUser(cachedUser);
      setIsOfflineReady(true);
    }
  }, []);

  // ── Suspension check ──
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
  useEffect(() => {
    const checkSuspension = async () => {
      const token = localStorage.getItem("erp_token");
      if (!token || !user) return;
      try {
        const response = await fetch(`${API_URL}/auth/check-suspension`, {
          headers: { "x-auth-token": token }
        });
        if (response.status === 403) {
          const data = await response.json();
          if (data.error === "suspended") {
            api.clearToken();
            localStorage.removeItem("erp_token");
            setUser(null);
            showAlert("Your business has been suspended. Contact your administrator.", "error");
          }
        }
      } catch (e) {
        console.error("Suspension check failed:", e);
      }
    };
    if (user) {
      checkSuspension();
      const interval = setInterval(checkSuspension, 30000);
      window.addEventListener("focus", checkSuspension);
      return () => {
        clearInterval(interval);
        window.removeEventListener("focus", checkSuspension);
      };
    }
  }, [user]);

  // ── Auth init ──
  useEffect(() => {
    // A granular platform-admin invitation uses its own one-time token in
    // the query string. This ERP-login initializer must not consume or strip
    // it, otherwise the next render falls back to ordinary portal login.
    const routePath = window.location.pathname.replace(/\/+$/, '') || '/';
    if (routePath === '/admin-portal/setup') return;

    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const sel = params.get("selector");
    const createBusiness = params.get("createBusiness") === "1";
    const err = params.get("error");
    const currentHash = window.location.hash;
    const currentPath = window.location.pathname; // preserve /admin (or any other real route)
    window.history.replaceState({}, "", currentPath + currentHash);

    if (err) { setUrlError(err); setUser(null); return; }

    if (sel) {
      localStorage.setItem("erp_selector", sel);
      fetch(`${process.env.REACT_APP_API_URL || "http://localhost:5000"}/auth/me`, {
        headers: { "x-auth-token": sel },
      })
        .then(r => r.json())
        .then(d => {
          if (d.role === "selector") setSelectorData({ token: sel, businesses: d.businesses, name: d.name, startRegistration: createBusiness });
          else setUser(null);
        })
        .catch(() => setUser(null));
      return;
    }

    if (token) api.saveToken(token);
    const stored = localStorage.getItem("erp_token");
    if (!stored) { setUser(null); return; }

    api.getMe()
      .then(async (u) => {
        if (u.role === "selector") { setUser(null); return; }

        // /admin deliberately does not reuse a normal ERP business session,
        // even when that business account belongs to the platform owner.
        // The dedicated admin OAuth flow issues role "platform_admin".
        if (isAdminRoute && u.role !== "platform_admin") {
          setIsAdminUser(false);
          setUser(null);
          return;
        }

        // "unregistered" — a real, valid session with no business yet
        // (see server.js's GoogleStrategy verify callback). Land them in
        // the app so RegisterBusiness can render below, rather than the
        // old "denied" behaviour of discarding the session entirely.
        // None of the business-dependent tab/policy logic further down
        // this effect applies yet — there's no business to compute it
        // against.
        if (u.role === "unregistered") {
          // Platform administration is independent of ERP business membership.
          // A platform owner may legitimately have no business at all, so resolve
          // admin authority before rendering either /admin or business setup.
          const adminStatus = await api.isAdmin().catch(() => ({ isAdmin: false }));
          setIsAdminUser(adminStatus.isAdmin);
          setUser(u);
          return;
        }

        localStorage.setItem("cached_user", JSON.stringify(u));
        
        // Normal login flow
        const hash = u.isBusinessOwner && new URLSearchParams(window.location.search).has('paymentSetup')
          ? 'plan' : window.location.hash.slice(1);
        const adminStatus = await api.isAdmin().catch(() => ({ isAdmin: false }));
        setIsAdminUser(adminStatus.isAdmin);
        const allowedTabs = getAllowedTabsForUser(u, adminStatus.isAdmin);
        if (allowedTabs.includes(hash)) {
          setTab(hash);
        } else {
          setTab(getDefaultTabForUser(u));
        }
        try { setPolicyAccepted(localStorage.getItem(getPolicyKey(u)) === "yes"); } catch (e) { setPolicyAccepted(true); }
        setShowWelcome(!!token);
        setUser(u);
        saveToCache('user', u);
      })
      .catch(e => {
        console.log("Auth error:", e);
        if (!navigator.onLine) {
          const cachedUser = getFromCache('user');
          if (cachedUser) {
            try {
              const cached = JSON.parse(cachedUser);
              setUser(cached);
              console.log("📱 Offline mode - using cached user data");
            } catch (err) {
              console.error("Failed to parse cached user:", err);
              setUser(null);
            }
          } else {
            setUser(null);
          }
        } else if (e?.message === "suspended" || e?.message?.includes("suspended")) {
          api.clearToken();
          localStorage.removeItem("erp_token");
          setUrlError("suspended");
          setUser(null);
        } else {
          // A valid OAuth token can still hit a backend runtime failure.
          // Do not silently present that as an ordinary signed-out state:
          // keep the token so a refresh can recover after the backend is fixed,
          // and surface a distinct service error on the login screen.
          if (e?.status >= 500) setUrlError("server");
          setUser(null);
        }
      });
  }, []);




  // ── Offline sync service ──
  useEffect(() => {
    const cleanup = startSyncService();
    return cleanup;
  }, []);

  // ── Auto-cache data ──
  useEffect(() => {
    if (!user || !navigator.onLine) return;
    const cacheAllData = async () => {
      console.log("📦 Caching all data for offline use...");
      try { const orders = await api.getOrders({ limit: 200 }); cacheOrders(orders); } catch (e) { console.log("❌ Orders cache failed"); }
      try { const payments = await api.getPayments({ limit: 200 }); cachePayments(payments); } catch (e) { console.log("❌ Payments cache failed"); }
      try { const expenses = await api.getExpenses({ limit: 200 }); cacheExpenses(expenses); } catch (e) { console.log("❌ Expenses cache failed"); }
      try { const inventory = await api.getInventory({ limit: 200 }); cacheInventory(inventory); } catch (e) { console.log("❌ Inventory cache failed"); }
      try { const products = await api.getFinishedGoods({ activeOnly: "true" }); cacheFinishedGoods(products); } catch (e) { console.log("❌ Products cache failed"); }
      try { const capital = await api.getCapital(); cacheCapital(capital); } catch (e) { console.log("❌ Capital cache failed"); }
      try { const assets = await api.getAssets(); cacheAssets(assets); } catch (e) { console.log("❌ Assets cache failed"); }
      try { const ambassadors = await api.getAmbassadors(); cacheAmbassadors(ambassadors); } catch (e) { console.log("❌ Ambassadors cache failed"); }
      try { const dashboard = await api.getDashboard(); cacheDashboard(dashboard); } catch (e) { console.log("❌ Dashboard cache failed"); }
      console.log("🎉 Offline cache update complete");
    };
    cacheAllData();
  }, [user]);

  // ── Hash change listener ──
  useEffect(() => {
    const handleHashChange = () => {
      // /admin owns its own hash-based navigation (AdminPortal). Do not
      // reinterpret admin pages such as #requests as ERP tabs and rewrite
      // them to #dashboard; that created a hash-change loop and flicker.
      if (window.location.pathname === '/admin' || window.location.pathname === '/admin-portal') return;
      const hash = window.location.hash.slice(1);
      const allowedTabs = getAllowedTabsForUser(user, isAdminUser);
      if (allowedTabs.includes(hash)) {
        setTab(hash);
      } else {
        // Hash points to a tab this user isn't allowed to see — don't switch
        // tabs, and reset the URL hash back to whatever's actually showing
        // so the address bar doesn't lie about what's on screen.
        window.location.hash = tab;
      }
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [user, isAdminUser, tab]);

  

  // ── Logout ──
  const handleLogout = async () => {
    await api.logout().catch(() => {});
    api.clearToken();
    localStorage.removeItem("erp_selector");
    sessionStorage.removeItem('admin_login');
    localStorage.removeItem('admin_login')
    localStorage.removeItem('admin_intent');
    setUser(null); 
    setSelectorData(null); 
    setIsAdminUser(false); 
    setShowWelcome(false); 
    setPolicyAccepted(true);
  };
  // Granular admin accounts (Tier 12) — a separate identity from both
  // ERP staff and the /admin superadmin panel below, so this check is
  // deliberately placed alongside the other standalone-identity portals
  // above (all early-return, none depend on the ERP `user` state).
  if (['/admin-portal', '/admin-portal/setup', '/admin-portal/reset'].includes(window.location.pathname.replace(/\/+$/, '') || '/')) {
    return <AdminAccountPortal />;
  }
  if (window.location.pathname === '/privacy-policy') {
    return <PrivacyPolicy />;
  }

  if (window.location.hash.startsWith("#password-setup=")) return <PasswordAccess />;

  // ── Loading state ──
  if (user === undefined && !selectorData) {
    return (
      <><style>{css}</style>
        <div className="login-screen"><div className="login-box">
          <img src={PRODUCT_BRAND.splash} alt={PRODUCT_BRAND.name} style={{ width: 190, maxWidth: "60vw", height: "auto", objectFit: "contain", margin: "0 auto" }} />
          <p style={{ marginTop: 12, color: "#6b7280" }}>Loading…</p>
        </div></div></>
    );
  }

  // ========== ✅ ADMIN PORTAL CHECK ==========
  // /admin is a dedicated superadmin route; business registration never owns it.
  if (window.location.pathname === '/admin') {
    // If user is logged in AND is admin, show admin portal
    if (user && isAdminUser) {
      return <AdminPortal showAlert={showAlert} />;
    }
    
    // If user is logged in but NOT admin, redirect to dashboard
    if (user && !isAdminUser) {
      // Previously a silent `window.location.href = '/'` — from the
      // person's side that looked exactly like being bounced back to
      // the normal ERP login for no reason, especially confusing for
      // someone who was just added as a platform admin with a specific
      // role (a completely different account type, AdminUser/JWT, that
      // this ADMIN_EMAIL-gated page has no way to recognize even after
      // a successful Google sign-in). Route them to the right place
      // with an explanation instead of just disappearing.
      return (
        <div className="login-screen"><div className="login-box">
          <div style={{ fontSize: 56, marginBottom: 4 }}>🚫</div>
          <h2>Not a superadmin</h2>
          <p>{user.email} isn't on the superadmin list for this platform.</p>
          <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 12 }}>
            Were you added as a platform admin with a specific role instead? That's a separate login — head to the <a href="/admin-portal">Admin Portal</a>.
          </p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => { window.location.href = '/'; }}>
            Go to my dashboard instead
          </button>
        </div></div>
      );
    }
    
    // If user is NOT logged in, show admin login screen
    if (!user) {
      return <LoginScreen urlError={urlError} adminMode={true} />;
    }
  }

  // ── Business selector ──
  if (selectorData) {
    return (
      <BusinessSelector
        selectorToken={selectorData.token}
        businesses={selectorData.businesses}
        name={selectorData.name}
        startRegistration={selectorData.startRegistration}
        onSelect={userData => {
          setSelectorData(null);
          setTab(getDefaultTabForUser(userData));
          try { setPolicyAccepted(localStorage.getItem(getPolicyKey(userData)) === "yes"); } catch (e) { setPolicyAccepted(true); }
          setShowWelcome(true);
          setUser(userData);
          setCartCount(0);
          api.isAdmin().then(r => setIsAdminUser(r.isAdmin)).catch(() => {});
        }}
      />
    );
  }

  // ── Not logged in ──
  if (!user) {
    return (
      <LoginScreen urlError={urlError} />
    );
  }

  // ── No business yet — self-service registration ──
  // Replaces the old "Access Denied / not assigned a role" wall: this
  // person's Google sign-in is perfectly valid, they just haven't set up
  // a business yet (see server.js's GoogleStrategy verify callback and
  // POST /auth/register). No admin approval to wait on.
  if (user.role === "unregistered") {
    return (
      <RegisterBusiness
        user={user}
        onLogout={handleLogout}
        onRegistered={async () => {
          const u = await api.getMe();
          setTab(getDefaultTabForUser(u));
          setUser(u);
          setShowWelcome(true);
        }}
      />
    );
  }

  const isAdmin = isAdminUser;

  if (!policyAccepted && !isAdmin) {
    return (
      <PolicyPage
        user={user}
        onAccept={() => {
          try { localStorage.setItem(getPolicyKey(user), "yes"); } catch(e) {}
          setPolicyAccepted(true);
        }}
        onSignOut={handleLogout}
      />
    );
  }

  if (showWelcome && !isAdmin) {
    return (
      <WelcomePage
        user={user}
        onContinue={() => setShowWelcome(false)}
      />
    );
  }

  // ── Feature flags ──
  const f = user.features || {
    hasOrders: true, hasRawMaterials: true, hasWalkinSales: true,
    hasAmbassadors: true, hasProfitAlloc: true, hasAssets: true,
    salesCanLogPayments: false,
  };
  const isDemoWorkspace = user.workspaceContext?.mode === "demo";

  // ── Nav items ──
  const navItems = user.role === "manager" ? [
    { key: "dashboard", label: "📊 Dashboard" },
    ...(f.hasOrders ? [{ key: "orders", label: "📦 Orders" }, { key: "payments", label: "💳 Payments" }] : []),
    ...(f.hasWalkinSales ? [{ key: "finishedgoods", label: "📦 Products" }, { key: "newsale", label: "🛒 New Sale" }, { key: "pendingsales", label: "✅ Confirm Sales" }, { key: "saleslog", label: "📋 Sales Log" }, { key: "forecast", label: "📈 Demand Forecast" }] : []),    ...(f.hasRawMaterials ? [{ key: "inventory", label: "🧱 Raw Inventory" }] : []),
    { key: "expenses", label: "🧾 Expenses" },
    ...(!user.shopId ? [{ key: "capital", label: "💰 Capital" }] : []),
    { key: "transfers", label: "🔁 Transfers" },
    ...(f.hasAmbassadors ? [{ key: "ambassadors", label: "🏅 Ambassadors" }] : []),
    ...(!user.shopId && f.hasProfitAlloc ? [{ key: "allocation", label: "📈 Profit" }] : []),
    ...(!user.shopId && f.hasAssets ? [{ key: "assets", label: "🏭 Assets" }] : []),
    { key: "customers", label: "👤 Customers" },
    ...(user.isBusinessOwner || (user.role === "manager" && !user.shopId) ? [{ key: "shops", label: "🏬 Shops" }] : []),
    ...(!user.shopId ? [
      { key: "history", label: "📅 History" },
      { key: "audit", label: "🔍 Audit Log" },
    ] : []),
    { key: "ledger", label: "📒 Ledger" },
    ...(!isDemoWorkspace ? [{ key: "aianalysis", label: "🤖 AI Analysis" }] : []),
    ...(!user.shopId ? [
      { key: "users", label: "👥 Users" },
      ...(!isDemoWorkspace ? [{ key: "marketplace-settings", label: "🌐 Marketplace Setup" }] : []),
      { key: "settings", label: "⚙️ Settings" },
      ...(!isDemoWorkspace ? [{ key: "plan", label: "💳 Plan & Billing" }] : []),
    ] : []),
    ...(isAdminUser ? [{ key: "admin", label: "🛡 Admin Portal" }] : []),
  ] : user.role === "sales" ? [
    ...(f.hasOrders ? [{ key: "orders", label: "📦 Orders" }] : []),
    ...(f.salesCanLogPayments && f.hasOrders ? [{ key: "payments", label: "💳 Payments" }] : []),
    { key: "saleslog", label: "📋 Sales Log" },    ...(f.hasWalkinSales ? [{ key: "newsale", label: "🛒 New Sale" }] : []),
    { key: "products", label: "🛍️ Products" },
    { key: "customers", label: "👤 Customers" },
  ] : user.role === "cashier" ? [
    ...(f.hasOrders ? [{ key: "payments", label: "💳 Payments" }] : []),
    ...(f.hasWalkinSales ? [{ key: "newsale", label: "🛒 New Sale" }, { key: "pendingsales", label: "✅ Confirm Sales" }] : []),
    ...(f.hasRawMaterials ? [{ key: "inventory", label: "🧱 Raw Inventory" }] : []),    { key: "expenses", label: "🧾 Expenses" },
    { key: "saleslog", label: "📋 Sales Log" },
    { key: "products", label: "🛍️ Products" },
  ] : user.role === "ambassador" ? [
    { key: "ambdash", label: "📊 My Dashboard" },
    { key: "products", label: "🛍️ Products" },  ] : user.role === "accountant" ? [
    { key: "dashboard", label: "📊 Dashboard" },
    ...(f.hasOrders ? [{ key: "orders", label: "📦 Orders" }] : []),
    ...(f.hasWalkinSales ? [{ key: "saleslog", label: "📋 Sales Log" }] : []),
    { key: "expenses", label: "🧾 Expenses" },
    ...(!user.shopId ? [{ key: "capital", label: "💰 Capital" }] : []),
    { key: "transfers", label: "🔁 Transfers" },
    { key: "ledger", label: "📒 Ledger" },
    { key: "history", label: "📅 History" },
    { key: "aianalysis", label: "🤖 AI Analysis" },
  ] : [];

  // Render-time security gate — the actual enforcement point. Even if `tab`
  // state were somehow set to something this user shouldn't see (devtools,
  // a stale hash, a future bug in the routing checks above), the matching
  // component below still won't render unless the tab is in this list.
  const allowedTabs = getAllowedTabsForUser(user, isAdminUser);
  const canViewTab = (key) => allowedTabs.includes(key);

  const sp = { showAlert, user };
  // Trial uses the universal platform brand. Starter and above use the
  // business's own name/logo when available, with the universal brand as
  // fallback so the browser/sidebar never become blank.
  const bizName = PRODUCT_BRAND.name;
  const bizLogo = PRODUCT_BRAND.symbol;

  const navigateTo = (key) => {
    if (key === "admin") {
      window.location.href = "/admin";
      return;
    }
    setTab(key);
    window.location.hash = key;
    setCartCount(0);
    setSidebarOpen(false);
  };

  return (
    <>
      <style>{css}</style>
      <div className="app-shell">
        {sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 199 }}
          />
        )}

        {/* ── SIDEBAR ── */}
        <div className={`sidebar${sidebarOpen ? ' open' : ''}`} style={{
          position: 'fixed', top: 0, left: 0, bottom: 0, width: '240px', zIndex: 200,
          background: 'var(--sidebar-bg)', display: 'flex', flexDirection: 'column',
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform 0.28s ease',
        }}>
          <div className="sidebar-header">
            {bizLogo
              ? <img src={bizLogo} alt={bizName} className="sidebar-logo" />
              : <div style={{ width: 34, height: 34, borderRadius: 8, background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 16, flexShrink: 0 }}>
                {bizName[0]}
              </div>
            }
            <div style={{ overflow: "hidden" }}>
              <div className="sidebar-biz-name">{bizName}</div>
              <div className="sidebar-plan">{user.role?.toUpperCase()}</div>
            </div>
          </div>

          <div className="sidebar-nav" style={{ flex: 1, overflowY: 'auto' }}>
            <SidebarNav
              navItems={navItems}
              tab={tab}
              setTab={navigateTo}
              notifCounts={notifCounts}
              isManager={user.role === "manager"}
            />
          </div>

          <div className="sidebar-footer">
            <div className="sidebar-user">
              <div className="sidebar-user-info">
                <div className="sidebar-user-name">{user.name || user.email}</div>
                <div className="sidebar-user-role">{user.email}</div>
              </div>
              <NotificationBell
                user={user}
                currentTab={tab}
                onNavigate={navigateTo}
                onCounts={setNotifCounts}
              />
            </div>

            <button className="theme-toggle" onClick={() => setDarkMode(d => !d)}>
              <span>{darkMode ? "☀️" : "🌙"}</span>
              <span>{darkMode ? "Light Mode" : "Dark Mode"}</span>
              <div className={`toggle-track ${darkMode ? "on" : ""}`}>
                <div className="toggle-thumb" />
              </div>
            </button>

            <UserGuide user={user} />

            <PasswordAccess account isOwner={user.isBusinessOwner} />
            <button className="sidebar-logout" onClick={handleLogout}>Sign Out</button>
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer"
               style={{ display: "block", textAlign: "center", fontSize: 10.5, color: "var(--sidebar-text)", marginTop: 10, textDecoration: "underline", opacity: 0.8 }}>
              Privacy Policy
            </a>
          </div>
        </div>

        {/* ── TOPBAR ── */}
        <div className="topbar" style={{
          position: 'fixed', top: 0, left: 0, right: 0, height: '52px',
          background: 'var(--topbar-bg)', borderBottom: '1px solid var(--topbar-border)',
          display: 'flex', alignItems: 'center', padding: '0 16px', gap: '10px', zIndex: 100,
        }}>
          <button className="topbar-toggle" onClick={() => setSidebarOpen(o => !o)}>
            <span /><span /><span />
          </button>
          {tab !== getDefaultTabForUser(user) && (
            <button
              onClick={() => navigateTo(getDefaultTabForUser(user))}
              title="Back to Dashboard"
              aria-label="Back to Dashboard"
              style={{
                background: 'var(--surface2)', border: '1px solid var(--topbar-border)', borderRadius: 8,
                width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', flexShrink: 0, color: 'var(--text)', fontSize: 16, padding: 0,
              }}
            >
              ←
            </button>
          )}
          <span className="topbar-page-title" style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)', flex: 1 }}>
            {navItems.find(n => n.key === tab)?.label?.replace(/^\S+\s/, "") || "Dashboard"}
          </span>
          <DemoWorkspaceSwitcher user={user} />
          {(tab === "newsale" || tab === "ambdash") && cartCount > 0 && (
            <button
              onClick={() => { const el = document.getElementById("cart-section"); if (el) el.scrollIntoView({ behavior: "smooth" }); }}
              style={{ position: "relative", background: "var(--accent)", border: "none", borderRadius: 10, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, color: "#fff", fontSize: 18 }}
            >
              🛒
              <span style={{
                position: "absolute", top: -6, right: -6, background: "#10b981", color: "#fff", borderRadius: 999, minWidth: 18, height: 18,
                fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px", border: "2px solid var(--topbar-bg)",
              }}>
                {cartCount}
              </span>
            </button>
          )}
          <span className="role-badge" style={{ background: 'var(--accent)', color: '#fff', fontSize: '10px', padding: '3px 8px', borderRadius: '20px', fontWeight: '600', flexShrink: 0 }}>
            {user.role?.toUpperCase()}
          </span>
        </div>

        {/* ── MAIN CONTENT ── */}
        <div className="main-area" style={{ marginTop: '52px', minHeight: 'calc(100vh - 52px)', padding: '16px', width: '100%', boxSizing: 'border-box' }}>
          {alert && (
            <div style={{ marginBottom: 16 }}>
              <div className={`alert alert-${alert.type}`}>{alert.msg}</div>
            </div>
          )}

          <div className="content" style={{ padding: 0 }}>
            <DemoWorkspaceBanner user={user} />
            {user.workspaceContext?.mode !== 'demo' && <GrowthCampaignBanner user={user} />}
            {user.workspaceContext?.mode !== 'demo' && user.isBusinessOwner && tab === 'plan' && <OnlinePaymentSetup />}
            <React.Suspense fallback={<div style={{ padding: 24, color: "var(--text-muted)" }}>Loading module…</div>}>
            {tab === "dashboard" && canViewTab("dashboard") && <Dashboard {...sp} />}
            {tab === "ambdash" && canViewTab("ambdash") && <AmbassadorDashboard {...sp} onCartChange={setCartCount} />}
            {tab === "orders" && canViewTab("orders") && <Orders {...sp} />}
            {tab === "newsale" && canViewTab("newsale") && <NewSale {...sp} onCartChange={setCartCount} />}
            {tab === "payments" && canViewTab("payments") && <Payments {...sp} />}
            {tab === "expenses" && canViewTab("expenses") && <Expenses {...sp} />}
            {tab === "inventory" && canViewTab("inventory") && <Inventory {...sp} />}
            {tab === "pendingsales" && canViewTab("pendingsales") && <PendingSales {...sp} />}
            {tab === "saleslog" && canViewTab("saleslog") && <SalesLog {...sp} />}
            {tab === "finishedgoods" && canViewTab("finishedgoods") && <FinishedGoods {...sp} />}
            {tab === "forecast" && canViewTab("forecast") && <DemandForecast {...sp} />}
            {tab === "products" && canViewTab("products") && <ProductsCatalogView {...sp} />}
            {tab === "capital" && canViewTab("capital") && <CapitalModule {...sp} />}
            {tab === "transfers" && canViewTab("transfers") && <InternalTransfers {...sp} />}
            {tab === "ambassadors" && canViewTab("ambassadors") && <AmbassadorMonitor {...sp} />}
            {tab === "allocation" && canViewTab("allocation") && <Allocation {...sp} />}
            {tab === "assets" && canViewTab("assets") && <Assets {...sp} />}
            {tab === "customers" && canViewTab("customers") && <Customers {...sp} />}
            {tab === "shops" && canViewTab("shops") && <Shops {...sp} />}
            {tab === "audit" && canViewTab("audit") && <AuditLog {...sp} />}
            {tab === "ledger" && canViewTab("ledger") && <Ledger {...sp} />}
            {tab === "history" && canViewTab("history") && <History {...sp} />}
            {tab === "users" && canViewTab("users") && <UsersModule {...sp} />}
            {tab === "aianalysis" && canViewTab("aianalysis") && <AIBusinessAnalysis {...sp} />}
            {tab === "marketplace-settings" && canViewTab("marketplace-settings") && user.role === "manager" && <MarketplaceSettings {...sp} />}
            {tab === "settings" && canViewTab("settings") && user.role === "manager" && <><FeaturesManager {...sp} onFeaturesUpdated={features => setUser(u => ({ ...u, features }))} /><DemoWorkspaceManager user={user} showAlert={showAlert} /></>}
            {tab === "plan" && canViewTab("plan") && user.role === "manager" && <PlanBilling {...sp} />}
            {!canViewTab(tab) && (
              <div style={{ padding: 40, textAlign: "center", color: "var(--text-faint)" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>You don't have access to this page</div>
                <div style={{ fontSize: 13 }}>Ask a manager if you believe this is a mistake.</div>
              </div>
            )}
            </React.Suspense>
          </div>
        </div>

        {!navigator.onLine && (
          <div style={{
            position: 'fixed', bottom: 16, left: 16, right: 16, background: '#f59e0b', color: '#fff',
            padding: '8px 16px', borderRadius: 8, fontSize: 12, textAlign: 'center', zIndex: 1000
          }}>
            ⚠️ You are offline. Showing cached data. Changes will sync when online.
          </div>
        )}
      </div>
    </>
  );
}


